import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Home, HardHat, Building2, Compass, Hammer, ChevronLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../utils/api';
import { toast } from 'sonner';

const RoleSelection = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedEngineerType, setSelectedEngineerType] = useState(null);
  const [step, setStep] = useState(1); // 1 = role, 2 = engineer type
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      
      // If user already has a role set (not pending), redirect to dashboard
      if (response.data.role && response.data.role !== 'pending') {
        redirectToDashboard(response.data.role);
      }
    } catch (error) {
      // Not logged in, redirect to login
      navigate('/login');
    }
  };

  const redirectToDashboard = (role) => {
    switch (role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'engineer':
        navigate('/engineer');
        break;
      case 'customer':
      default:
        navigate('/customer');
        break;
    }
  };

  const handleSelectRole = async () => {
    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    // If engineer selected, show engineer type selection
    if (selectedRole === 'engineer' && step === 1) {
      setStep(2);
      return;
    }

    // For engineer, validate engineer type
    if (selectedRole === 'engineer' && !selectedEngineerType) {
      toast.error('Please select your engineer type');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/select-role', { 
        role: selectedRole,
        engineer_type: selectedRole === 'engineer' ? selectedEngineerType : undefined
      });
      toast.success('Welcome! Your account is ready.');
      redirectToDashboard(selectedRole);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set role');
    } finally {
      setSubmitting(false);
    }
  };

  const roles = [
    {
      id: 'customer',
      title: 'Homeowner',
      description: 'I am building my home and want to track the construction progress',
      icon: Home,
      color: 'bg-blue-50 border-blue-200 hover:border-blue-400',
      selectedColor: 'bg-blue-100 border-blue-500 ring-2 ring-blue-500',
      iconColor: 'text-blue-600'
    },
    {
      id: 'engineer',
      title: 'Engineer',
      description: 'I manage design plans or construction sites',
      icon: HardHat,
      color: 'bg-orange-50 border-orange-200 hover:border-orange-400',
      selectedColor: 'bg-orange-100 border-orange-500 ring-2 ring-orange-500',
      iconColor: 'text-orange-600'
    }
  ];

  const engineerTypes = [
    {
      id: 'design',
      title: 'Design Engineer',
      description: 'I work on architectural plans, structural drawings, and design documentation',
      icon: Compass,
      color: 'bg-purple-50 border-purple-200 hover:border-purple-400',
      selectedColor: 'bg-purple-100 border-purple-500 ring-2 ring-purple-500',
      iconColor: 'text-purple-600'
    },
    {
      id: 'construction',
      title: 'Construction Engineer',
      description: 'I manage on-site construction work and project execution',
      icon: Hammer,
      color: 'bg-green-50 border-green-200 hover:border-green-400',
      selectedColor: 'bg-green-100 border-green-500 ring-2 ring-green-500',
      iconColor: 'text-green-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-stone-200 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <Card className="border-0 shadow-2xl rounded-2xl overflow-hidden">
          <CardHeader className="text-center pb-2 pt-8">
            <div className="mx-auto w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-stone-900">
              {step === 1 ? (
                <>Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!</>
              ) : (
                'What type of engineer?'
              )}
            </CardTitle>
            <CardDescription className="text-stone-600 text-base mt-2">
              {step === 1 ? (
                'How will you be using MyHomeEngineer?'
              ) : (
                'Select your specialization'
              )}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6 space-y-4">
            {step === 1 ? (
              // Step 1: Role Selection
              <>
                {roles.map((role) => (
                  <motion.button
                    key={role.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedRole(role.id)}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                      selectedRole === role.id ? role.selectedColor : role.color
                    }`}
                    data-testid={`role-${role.id}`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg ${selectedRole === role.id ? 'bg-white' : 'bg-white/80'}`}>
                        <role.icon className={`h-6 w-6 ${role.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-stone-900 text-lg">{role.title}</h3>
                        <p className="text-stone-600 text-sm mt-1">{role.description}</p>
                      </div>
                      {selectedRole === role.id && (
                        <div className="w-5 h-5 bg-stone-900 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </>
            ) : (
              // Step 2: Engineer Type Selection
              <>
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center text-stone-600 hover:text-stone-900 mb-2"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
                
                {engineerTypes.map((type) => (
                  <motion.button
                    key={type.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedEngineerType(type.id)}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                      selectedEngineerType === type.id ? type.selectedColor : type.color
                    }`}
                    data-testid={`engineer-type-${type.id}`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg ${selectedEngineerType === type.id ? 'bg-white' : 'bg-white/80'}`}>
                        <type.icon className={`h-6 w-6 ${type.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-stone-900 text-lg">{type.title}</h3>
                        <p className="text-stone-600 text-sm mt-1">{type.description}</p>
                      </div>
                      {selectedEngineerType === type.id && (
                        <div className="w-5 h-5 bg-stone-900 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </>
            )}

            <Button
              onClick={handleSelectRole}
              disabled={(step === 1 && !selectedRole) || (step === 2 && !selectedEngineerType) || submitting}
              className="w-full h-12 mt-6 bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white rounded-xl text-base font-medium"
              data-testid="continue-button"
            >
              {submitting ? (
                <span className="flex items-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Setting up...
                </span>
              ) : (
                step === 1 && selectedRole === 'engineer' ? 'Next' : 'Continue'
              )}
            </Button>

            <p className="text-center text-xs text-stone-500 mt-4">
              You can contact your admin if you need to change your role later.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RoleSelection;
